import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import JsonLd from "@/components/base/JsonLd";
import { comics, getComicById } from "@/mocks/comics";

const site = "https://ymdbo14.cn";

export default function ComicDetail() {
  const { id } = useParams();
  const comic = id ? getComicById(id) : undefined;

  const related = useMemo(() => {
    if (!comic) return [];
    return comics.filter((c) => c.categoryId === comic.categoryId && c.id !== comic.id).slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <section className="flex min-h-[60vh] flex-col items-center justify-center bg-background-50 px-4 pt-20 text-center">
        <i className="ri-file-search-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 text-xl font-bold text-foreground-900">未找到这部漫画</h1>
        <p className="mt-2 text-sm text-foreground-500">该漫画可能已下架或链接有误</p>
        <Link to="/category/all" className="mt-6 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600">
          返回漫画大全
        </Link>
      </section>
    );
  }

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: comic.title,
          image: comic.cover,
          datePublished: comic.chapters[0]?.date,
          dateModified: comic.updateTime,
          inLanguage: "zh-CN",
          author: { "@type": "Person", name: comic.author },
          publisher: { "@type": "Organization", name: "漫画网" },
          articleSection: comic.categoryName,
          description: comic.intro,
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${site}/` },
            { "@type": "ListItem", position: 2, name: "漫画分类", item: `${site}/category/all` },
            { "@type": "ListItem", position: 3, name: `${comic.categoryName}漫画`, item: `${site}/category/${comic.categoryId}` },
            { "@type": "ListItem", position: 4, name: comic.title },
          ],
        }}
      />

      <section className="bg-secondary-100/70 pb-10 pt-24 md:pt-28">
        <div className="container-x">
          <Breadcrumb
            items={[
              { label: "漫画分类", to: "/category/all" },
              { label: `${comic.categoryName}漫画`, to: `/category/${comic.categoryId}` },
              { label: comic.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-[280px_1fr]">
            {/* Cover */}
            <div className="mx-auto w-full max-w-[280px] md:mx-0">
              <div className="relative aspect-[3/4] overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
                <img src={comic.cover} alt={`${comic.title} - ${comic.categoryName}漫画封面`} title={`${comic.title}漫画`} className="h-full w-full object-cover object-top" />
                <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-semibold text-background-50 ${comic.status === "连载中" ? "bg-primary-500" : "bg-secondary-600"}`}>
                  {comic.status}
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="flex flex-col">
              <h1 className="font-display text-2xl font-black text-foreground-900 md:text-4xl">{comic.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-600 md:text-sm">
                <span className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-100 px-3 py-1.5 font-semibold text-accent-800">
                  <i className="ri-star-fill text-accent-500"></i> {comic.rating} 分
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-eye-line text-primary-500"></i> {comic.views} 人气
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-user-3-line text-primary-500"></i> {comic.author}
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-refresh-line text-primary-500"></i> {comic.updateTime} 更新
                </span>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-100 px-3 py-1.5 text-xs font-semibold text-primary-700">{comic.categoryName}漫画</span>
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                    {t}
                  </span>
                ))}
              </div>

              <h2 className="mt-6 flex items-center gap-2 text-base font-bold text-foreground-900 md:text-lg">
                <i className="ri-book-open-line text-primary-500"></i> 漫画简介
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600 md:text-base">{comic.intro}</p>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={`#chapters`}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 md:text-base"
                >
                  <i className="ri-play-circle-line"></i> 开始阅读 · 第 {comic.chapters.length} 话
                </Link>
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 bg-background-50 px-6 py-3.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 md:text-base"
                >
                  <i className="ri-bookmark-line"></i> 收藏漫画
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter list */}
      <section id="chapters" className="bg-background-50 py-10 md:py-14" aria-labelledby="chapter-heading">
        <div className="container-x">
          <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-end">
            <h2 id="chapter-heading" className="flex items-center gap-2 font-display text-xl font-black text-foreground-900 md:text-2xl">
              <i className="ri-list-check-2 text-primary-500"></i> 漫画网漫画目录
            </h2>
            <p className="text-xs text-foreground-500 md:text-sm">共 {comic.chapters.length} 话 · 按最新更新排列</p>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[...comic.chapters].reverse().map((ch) => (
              <div
                key={ch.no}
                className="flex items-center gap-3 rounded-md border border-background-200/70 bg-background-50 p-3.5 transition-colors hover:border-primary-200 hover:bg-primary-50/40"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-xs font-bold text-secondary-800">
                  {String(ch.no).padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground-900">第 {ch.no} 话 · {ch.title}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{ch.date}</p>
                </div>
                <i className="ri-play-circle-fill text-lg text-primary-400"></i>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-background-100/70 py-10 md:py-14" aria-labelledby="related-heading">
          <div className="container-x">
            <div className="flex items-end justify-between">
              <h2 id="related-heading" className="font-display text-xl font-black text-foreground-900 md:text-2xl">
                同分类 · 相关漫画推荐
              </h2>
              <Link to={`/category/${comic.categoryId}`} className="flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700">
                更多{comic.categoryName}漫画 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}